import React, { useMemo, useState } from 'react'
import { Stack, useTheme } from '@mui/material'
import { gql, useQuery } from '@apollo/client'
import { useNavigate } from 'router'
import { useTranslation } from 'react-i18next'
import prettyBytes from 'pretty-bytes'

import { ACCOUNTS_LIMIT, useSites, useUser } from 'store'
import WidgetGenericStatistic from 'components/global/Widgets/WidgetGenericStatistic'
import WidgetInformations from 'components/global/Widgets/WidgetInformations'
import WidgetSingleInformation from 'components/global/Widgets/WidgetSingleInformation'
import WidgetLineInformation from 'components/global/Widgets/WidgetLineInformation'
import WidgetSingleLineInformation from 'components/global/Widgets/WidgetSingleLineInformation'
import WidgetAccessPointZones from 'components/global/Widgets/WidgetAccessPointZones'
import { toast } from 'components/global/CUK/Toast/Toast'

import ConnectedDevicesModal from './ConnectedDevices/ConnectedDevicesModal'
import { KpisHome} from 'pages/Projects/Dashboard/Kpis'
import KPIVoipAlarms from './KPIVoipAlarms'

export const OPERATION_CONTACT_CASES = gql`
  query ContactCases($sf_id: String!) {
    contact: findContact(sf_id: $sf_id) {
      count_cases
      count_team_cases  
    }
  }
`

const OPERATION_INFORMATIONS = gql`
  query Informations($sf_ids: [String!]) {
    sites_count: countSites(sf_ids: $sf_ids)
    contacts_count: countContacts(sf_ids: $sf_ids)
  }
`

const OPERATION_HOTSPOT_METRICS = gql`
  query HotspotMetrics($sf_ids: [String]) {
    counting: monitoringCounting(sf_ids: $sf_ids) {
      accesspoint_ok
      accesspoint_unreachable
      switch_ok
      switch_unreachable
      chromecast_ok
      chromecast_unreachable
      tv_ok
      tv_unreachable
      esl_ok
      esl_unreachable
      firewall_ok
      firewall_unreachable
      cctv_camera_ok
      cctv_camera_unreachable
      cctv_server_ok
      cctv_server_unreachable
      active_users
      production
      hibernation
      spare
      planned
      works
    }
    counting_band: monitoringCountingLines(sf_ids: $sf_ids) {
      nb_lines
      nb_lines_reachable
      bandwidth_download
      bandwidth_upload
    }
  }
`

const CAN_SHOW_EQUIPMENTS_SYSTEM_LINES = false

const KpisCases = () => {
  const [user] = useUser()
  const {t} = useTranslation()
  const { data, loading } = useQuery(OPERATION_CONTACT_CASES, {
    variables: {
      sf_id: user.sf_contact_id,
    }
  })

  return (
    <Stack
      direction={CAN_SHOW_EQUIPMENTS_SYSTEM_LINES ? 'column' : 'row'}
      sx={{
        width: CAN_SHOW_EQUIPMENTS_SYSTEM_LINES ? '40%' : '100%',
        height: '100%'
      }}
      gap={2}
    >
      <Stack sx={{flex: 1}}>
        <WidgetSingleInformation
          loading={loading}
          onClick={() => {
            window.open(process.env.REACT_APP_EXPERIENCE_CLOUD_URL, '_blank')
          }}
          number={data?.contact?.count_cases || 0}
          icon="fa-regular fa-message-exclamation"
          text={t('kpis_widgets.kpis_current_ticket', {
            count: data?.contact?.count_cases || 0,
          })}
          landscapeView={!CAN_SHOW_EQUIPMENTS_SYSTEM_LINES}
        />
      </Stack>
      <Stack sx={{flex: 1}}>
        <WidgetSingleInformation
          loading={loading}
          onClick={() => {
            window.open(process.env.REACT_APP_EXPERIENCE_CLOUD_URL, '_blank')
          }}
          number={data?.contact?.count_team_cases || 0}
          icon="fa-regular fa-users"
          text={t('kpis_widgets.kpis_current_team_ticket', {
            count: data?.contact?.count_team_cases || 0,
          })}
          landscapeView={!CAN_SHOW_EQUIPMENTS_SYSTEM_LINES}
        />
      </Stack>
    </Stack>
  )
}

const KpisFirewalls = ({
  loading,
  counting,
  onClick,
  stats,
  total,
  cursor = 'pointer'
}) => {
  const {t} = useTranslation()
  return (
    <Stack sx={{flex: 1}}>
      <WidgetGenericStatistic
        loading={loading}
        cursor={cursor}
        onClick={onClick}
        title={t('kpis_widgets.kpi_firewalls', {
          count:
            counting?.firewall_ok +
            counting?.firewall_unreachable,
        })}
        nbWarning={stats.nbWarning}
        nbSuccess={stats.nbSuccess}
        nbError={stats.nbError}
        total={total}
        icon="fa-light fa-shield"
        primaryWidget={true}
      />
    </Stack>
  )
}

export const KpisInformations = () => {
  const [user] = useUser()
  const [{selected}] = useSites()
  const {t} = useTranslation()
  const { data, loading } = useQuery(OPERATION_INFORMATIONS, {
    variables: {
      sf_ids: selected.map(({ sf_id }) => sf_id),
    },
  })

  const userLines = useMemo(() => [
    { title: t('kpis_widgets.kpis_user_informations.full_name'), value: `${user.first_name} ${user.last_name}` },
    { title: t('kpis_widgets.kpis_user_informations.email'), value: user.email },
    { title: t('kpis_widgets.kpis_user_informations.site_count', { count: data?.sites_count }), value: data?.sites_count },
    {
      title: t('kpis_widgets.kpis_user_informations.user', { count: data?.contacts_count }),
      value: data?.contacts_count
    },
  ], [user, data, t])


  return (
    <WidgetInformations
      loading={loading}
      label={t('kpis_widgets.kpis_widget_label_info')}
      labelIcon="fa-regular fa-circle-user"
      lines={userLines}
    />
  )
}

const KpIsAccount = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [{ selected, focus }] = useSites()
  const [user] = useUser()
  const [showConnectedDevicesModal, setShowConnectedDevicesModal] = useState(false)

  const isInternalView = useMemo(() => {
    return user.internal && selected.length === 0
  }, [user, selected])

  const canEquipmentClick = useMemo(() => {
    if (isInternalView) {
      return false
    }
    return selected.length
      ? selected.length < ACCOUNTS_LIMIT
      : user.accounts.filter(({is_site}) => is_site).length < ACCOUNTS_LIMIT
  }, [selected, user.accounts, isInternalView])
  const { data, loading } = useQuery(OPERATION_HOTSPOT_METRICS, {
    variables: {
      sf_ids: focus ? [focus.sf_id] : selected.map(({ sf_id }) => sf_id),
    },
  })

  const canShowConnectedDevices = useMemo(() => {
    return selected.length === 1 && (data?.counting?.active_users || 0) > 0
  }, [selected, data?.counting?.active_users])

  const sfIds = useMemo(() => {
    return focus ? [focus.sf_id] : selected.map(({ sf_id }) => sf_id)
  }, [focus, selected])


  const valueBandDownloadToGiga = useMemo(() => {
    if (data) {
      return prettyBytes(data.counting_band.bandwidth_download * 1_000)
    }
    return 0
  }, [data])

  const firewallsStat = useMemo(() => {
    if (data && data.counting) {
      return {
        nbWarning: data.counting.firewall_unreachable,
        nbSuccess: data.counting.firewall_ok,
        nbError: 0,
      }
    }
    return {}
  }, [data])

  const cctvsStat = useMemo(() => {
    if (data && data.counting) {
      return {
        nbWarning: data.counting.cctv_camera_unreachable,
        nbSuccess: data.counting.cctv_camera_ok,
        nbError: 0,
      }
    }
    return {}
  }, [data])

  const apsStat = useMemo(() => {
    if (data && data.counting) {
      return {
        nbWarning: data.counting.accesspoint_unreachable,
        nbSuccess: data.counting.accesspoint_ok,
        nbError: 0,
      }
    }
    return {}
  }, [data])

  const switchesStat = useMemo(() => {
    if (data && data.counting) {
      return {
        nbWarning: data.counting.switch_unreachable,
        nbSuccess: data.counting.switch_ok,
        nbError: 0,
      }
    }
    return {}
  }, [data])

  const chromecastStat = useMemo(() => {
    if (data && data.counting) {
      return {
        nbWarning: data.counting.chromecast_unreachable,
        nbSuccess: data.counting.chromecast_ok,
        nbError: 0,
      }
    }
    return {}
  }, [data])

  const tvStat = useMemo(() => {
    if (data && data.counting) {
      return {
        nbWarning: data.counting.tv_unreachable,
        nbSuccess: data.counting.tv_ok,
        nbError: 0,
      }
    }
    return {}
  }, [data])

  const firewallsTotal = firewallsStat.nbSuccess + firewallsStat.nbWarning + firewallsStat.nbError
  const apsTotal = apsStat.nbSuccess + apsStat.nbWarning + apsStat.nbError
  const switchesTotal = switchesStat.nbSuccess + switchesStat.nbWarning + switchesStat.nbError
  const chromecastTotal = chromecastStat.nbSuccess + chromecastStat.nbWarning + chromecastStat.nbError
  const tvTotal = tvStat.nbSuccess + tvStat.nbWarning + tvStat.nbError
  const cctvTotal = cctvsStat.nbSuccess + cctvsStat.nbWarning + cctvsStat.nbError
  const totalProduction = apsTotal + switchesTotal + chromecastTotal + tvTotal

  const systemLines = useMemo(() => {
    if (data && data.counting) {
      return [
        { icon: 'fa-regular fa-play', title: t('kpis_widgets.kpis_system_informations.production', { count: totalProduction }), value: totalProduction, isBold: true },
        { icon: 'fa-regular fa-moon', title: t('kpis_widgets.kpis_system_informations.hibernation'), value: data.counting.hibernation, isBold: true },
        { icon: 'fa-regular fa-box', title: t('kpis_widgets.kpis_system_informations.spare'), value: data.counting.spare, isBold: true },
        { icon: 'fa-regular fa-hourglass', title: t('kpis_widgets.kpis_system_informations.planned'), value: data.counting.planned, isBold: true },
        { icon: 'fa-regular fa-user-helmet-safety', title: t('kpis_widgets.kpis_system_informations.works'), value: data.counting.works, isBold: true },
      ].filter(line => line.value > 0)
    }
    return [{ icon: 'fa-regular fa-play', title: t('kpis_widgets.kpis_system_informations.production', { count: 12 }), value: 12, isBold: true },]
  }, [data, t, totalProduction])


  const lineInformationData = [
    {
      mainLabel: t('kpis_widgets.kpis_network_informations.bandwidth'),
      labelInfo: t('kpis_widgets.kpis_network_informations.download'),
      value: valueBandDownloadToGiga ? valueBandDownloadToGiga.slice(0, -2) : 0,
      subValue: valueBandDownloadToGiga ? `${valueBandDownloadToGiga.slice(-2, -1)}bps` : 'bps',
    },
    {
      mainLabel: t('kpis_widgets.kpis_network_informations.line', { count: data?.counting_band.nb_lines_reachable }),
      labelInfo: t('kpis_widgets.kpis_network_informations.total_lines'),
      value: `${data ? data.counting_band.nb_lines_reachable : 0}`,
      subValue: `/ ${data ? data?.counting_band.nb_lines : 0}`,
      tooltipContent: data?.counting_band.nb_lines - data?.counting_band.nb_lines_reachable > 1
        ? t('kpis_widgets.kpis_lines_informations.unreachable_line', { number: data?.counting_band.nb_lines - data?.counting_band.nb_lines_reachable, count: data?.counting_band.nb_lines - data?.counting_band.nb_lines_reachable })
        : null,
    },
  ]

  const goToSupervision = (pathName, filterType) => {
    navigate({ pathname: `/monitoring/infras/hardwares/${pathName}`, search: `?filter=${filterType}` })
  }

  const handleConnectedDevicesClick = () => {
    if (canShowConnectedDevices) {
      setShowConnectedDevicesModal(true)
    }
  }

  return (
    <Stack sx={{ 
      width: '100%',
      gap: theme.spacing(2),
      pb: 3
    }}>
      {
        (user.admin || user.roles.project) && (
          <KpisHome canClick={!isInternalView}/>
        )
      }

      {
        (Boolean(apsTotal && switchesTotal &&  firewallsTotal)) && (
            <Stack sx={{flex: 1}}>
              <KpisFirewalls
                loading={loading}
                counting={data?.counting}
                stats={firewallsStat}
                total={firewallsTotal}
                cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
                onClick={
                 canEquipmentClick ? (status) => goToSupervision('firewalls', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
                }
                />
            </Stack>
          )
        }
      <Stack
        sx={{
          flexDirection: (Boolean(apsTotal) && Boolean(switchesTotal)) ? {
            xs: 'column', 
            sm: 'row' 
          } : 'column',
          gap: theme.spacing(2),
        }}
      >
        {
          Boolean(!apsTotal || !switchesTotal) && (
            <Stack sx={{flex: 1}}>
              <KpisFirewalls
                loading={loading}
                counting={data?.counting}
                stats={firewallsStat}
                total={firewallsTotal}
                cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
                onClick={
                  canEquipmentClick ? (status) => goToSupervision('firewalls', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
                }
              />
            </Stack>
          )
        }
        {
          Boolean(apsTotal) && (
            <Stack sx={{ flex: 1}}>
              <WidgetGenericStatistic
                loading={loading}
                cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
                onClick={
                  canEquipmentClick ? (status) => goToSupervision('access_points', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
                }
                title={t('kpis_widgets.kpi_aps', {
                  count:
                    data?.counting.accesspoint_ok +
                    data?.counting.accesspoint_unreachable,
                })}
                nbWarning={apsStat.nbWarning}
                nbSuccess={apsStat.nbSuccess}
                nbError={apsStat.nbError}
                total={apsTotal}
                icon="fa-solid fa-wifi"
                primaryWidget={true}
              />
            </Stack>
          )
        }
        {
          Boolean(switchesTotal) && (
            <Stack sx={{ flex: 1 }}>
              <WidgetGenericStatistic
                loading={loading}
                cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
                onClick={
                  canEquipmentClick ? (status) => goToSupervision('switches', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
                }
                title={t('kpis_widgets.kpi_switches', {
                  count:
                    data?.counting.switch_ok + data?.counting.switch_unreachable,
                })}
                nbWarning={switchesStat.nbWarning}
                nbSuccess={switchesStat.nbSuccess}
                nbError={switchesStat.nbError}
                total={switchesTotal}
                icon="fa-regular fa-ethernet"
              />
            </Stack>
          )
        }
      </Stack>
      {CAN_SHOW_EQUIPMENTS_SYSTEM_LINES && (loading) && (
        <Stack sx={{ width: user.experience_cloud ? '60%' : '100%', alignSelf: 'start' }}>
          <WidgetInformations
            loading={loading}
            lines={systemLines}
            hasLabel={false}
            widgetTitle={t('kpis_widgets.kpis_system_informations.widget_title')}
          />
        </Stack>
      )}
      {user.roles.requests && user.experience_cloud && <KpisCases  />}

      <WidgetSingleLineInformation
        loading={loading}
        value={data?.counting.active_users || 0}
        mainLabel={t('kpis_widgets.kpis_network_informations.connected_devices', { count: data?.counting.active_users })}
        labelInfo={t('kpis_widgets.kpis_network_informations.last_5_minutes')}
        onClick={handleConnectedDevicesClick}
        disabled={!canShowConnectedDevices}
      />
      <KPIVoipAlarms />
      <WidgetAccessPointZones />
      <WidgetLineInformation
        hasLink={
          (!isInternalView && (user.admin || Boolean(user.roles.wan)))
          || (!isInternalView && canEquipmentClick)
      }
        loading={loading}
        lines={lineInformationData}
        cursor
        onClick={
          !user.admin && !user.roles.wan
            ? () => {}
            : canEquipmentClick ? () => navigate({pathname: '/monitoring/wan'}) : () => toast.error(t('kpis_widgets.accounts_limit'))
        }
      />
      <Stack
        sx={{
          flexDirection: (Boolean(chromecastTotal) && Boolean(tvTotal)) ? {
            xs: 'column', 
            sm: 'row' 
          } : 'column',
          gap: theme.spacing(2),
        }}
      >
        {
          Boolean(chromecastTotal) && (
            <Stack sx={{ flex: 1, gap: theme.spacing(2) }}>
              <WidgetGenericStatistic
                loading={loading}
                cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
                onClick={
                  canEquipmentClick ? (status) => goToSupervision('chromecasts', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
                }
                title={t('kpis_widgets.kpi_chromecasts', {
                  count:
                    data?.counting.chromecast_ok + data?.counting.chromecast_unreachable,
                })}
                nbWarning={chromecastStat.nbWarning}
                nbSuccess={chromecastStat.nbSuccess}
                nbError={chromecastStat.nbError}
                total={chromecastTotal}
                icon="fa-brands fa-chromecast"
              />
            </Stack>
          )
        }

        {
          Boolean(tvTotal) && (
            <Stack sx={{ flex: 1, gap: theme.spacing(2) }}>
              <WidgetGenericStatistic
                loading={loading}
                cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
                onClick={
                  canEquipmentClick ? (status) => goToSupervision('tvs', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
                }
                title={t('kpis_widgets.kpi_tvs_plural')}
                nbWarning={tvStat.nbWarning}
                nbSuccess={tvStat.nbSuccess}
                nbError={tvStat.nbError}
                total={tvTotal}
                icon="fa-regular fa-tv"
              />
            </Stack>
          )
        }
      </Stack>
      {
        Boolean(cctvTotal) && (
          <WidgetGenericStatistic
            loading={loading}
            cursor={isInternalView || !canEquipmentClick ? 'inherit' : 'pointer'}
            onClick={
              canEquipmentClick ? (status) => goToSupervision('cctv_cameras', status) : () => toast.error(t('kpis_widgets.accounts_limit'))
            }
            title={t('kpis_widgets.kpi_cctvs', {
              count:
                data?.counting.cctv_camera_ok
                + data?.counting.cctv_camera_unreachable,
            })}
            nbWarning={cctvsStat.nbWarning}
            nbSuccess={cctvsStat.nbSuccess}
            nbError={cctvsStat.nbError}
            total={cctvTotal}
            icon="fa-regular fa-camera-cctv"
          />
        )
      }
      {
        !isInternalView && <KpisInformations />
      }
      <ConnectedDevicesModal
        open={showConnectedDevicesModal}
        onClose={() => setShowConnectedDevicesModal(false)}
        sfIds={sfIds}
      />
    </Stack>
  )
}

export default KpIsAccount
